<template>
  <div class="relative">
    <div class="form-input--table pr-1">
      <div class="material-bar">
        <div class="material-bar--title">Add Material</div>
        <div class="input-group search">
          <input
            type="text"
            v-model="searchKeyWord"
            @input="handleSearch"
            placeholder=" Filter by Name, ID, Product Code, and Description"
          />
        </div>
      </div>
      <filterTag
        :tags="getTags"
        @new-tag="addNewTag"
        @clear="clearTags"
        @update-tags="updateTags"
        :type="1"
        :clear="clear"
        title="Add tags to filter"
      />
      <div class="material-table dtx-table mb-4" :style="{height: '420px'}">
        <table style="min-width: 800px">
          <thead>
            <tr>
              <th></th>
              <th>Product Code</th>
              <th>Material ID</th>
              <th>Material Name</th>
              <th>PLM ID</th>
              <th>ERP ID</th>
              <th>UOM</th>
              <th>Description</th>
              <th>Lead Time</th>
            </tr>
          </thead>
          <tbody v-if="!materialMasterLoading">
            <tr
              v-for="item in materialMasterData"
              :key="item.material_id"
            >
              <td>
                <img
                  @click="handleRemoveSelected(item)"
                  v-if="isItemSelected(item)"
                  src="~@/assets/images/icons/checked-icon.svg"
                  alt=""
                  :class="{ 'disabled-checkbox': formTypeId === 5 && !item.workflow_template }"
                />
                <img
                  @click="handleToggleSelected(item)"
                  v-else
                  src="~@/assets/images/icons/unchecked-icon.svg"
                  alt=""
                  :class="{ 'disabled-checkbox': formTypeId === 5 && !item.workflow_template }"
                />
              </td>
              <td v-overflow-tooltip>{{ item.material_product_code?.product_code || "--" }}</td>
              <td v-overflow-tooltip>{{ item.custom_material_id || "--" }}</td>
              <td v-overflow-tooltip>{{ item.material_name || "--" }}</td>
              <td v-overflow-tooltip>{{ item.plm_material_id || "--" }}</td>
              <td v-overflow-tooltip>{{ item.erp_material_id || "--" }}</td>
              <td v-overflow-tooltip>{{ item.material_unit_details?.name || "--" }}</td>
              <td v-overflow-tooltip>{{ item.material_description || "--" }}</td>
              <td>{{ item.lead_time || "--" }}</td>
            </tr>
          </tbody>
        </table>
        <div class="center p-6">
          <loading-circle v-if="materialMasterLoading" />
        </div>
      </div>
    </div>
    <div class="footer-box v-center">
      <pagination
        :length="totalCount"
        :perPage="perPage"
        :pageNumber="pageNumber"
        @selectPage="handleSelectPage"
      />
      <div class="mt-4">
        <button class="btn btn-black mx-2" @click="handleAdd">Add</button>
        <button class="btn btn-cancel mx-2" @click="handleCancel">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { GetMaterialMasterForBom } from '@/api'
import Pagination from '../common/pagination.vue'
import LoadingCircle from '../common/loadingCircle.vue'
import filterTag from '../common/filterTag.vue'
import { debounce } from '@/utils/debounce'
import { mapGetters, mapMutations } from 'vuex'
import config from '@/config'

export default {
  name: 'MaterialSelector',
  components: {
    Pagination,
    LoadingCircle,
    filterTag
  },
  props: {
    selectedMaterials: {
      type: Array,
      default: () => []
    },
    formTypeId: {
      type: Number
    },
    clear: {
      type: Boolean,
      default: false
    },
    feature: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      materialMasterData: [],
      materialMasterLoading: false,
      pageNumber: 1,
      perPage: 10,
      totalCount: 0,
      searchKeyWord: '',
      updateOnSearch: null
    }
  },
  computed: {
    ...mapGetters('tag', ['getTags'])
  },
  watch: {
    '$store.state.tag.tags': function () {
      this.fetchMaterialMasterData()
    }
  },
  methods: {
    ...mapMutations('tag', ['addNewTag', 'updateTags', 'clearAllTags']),

    isItemSelected (item) {
      return this.selectedMaterials.findIndex(
        (selectedItem) => selectedItem.material_id === item.id
      ) !== -1
    },

    handleToggleSelected (item) {
      this.$emit('toggle-selected', item)
    },

    handleRemoveSelected (item) {
      this.$emit('remove-selected', item)
    },

    handleSelectPage (page) {
      this.pageNumber = page
      this.fetchMaterialMasterData()
    },

    handleSearch () {
      this.updateOnSearch()
    },

    handleAdd () {
      this.$emit('add-materials')
    },

    handleCancel () {
      this.$emit('cancel')
    },

    clearTags () {
      this.$emit('clear-tags')
      this.clearAllTags()
    },

    fetchMaterialMasterData () {
      this.materialMasterLoading = true
      const filter = {
        jump: (this.pageNumber - 1) * this.perPage,
        perPage: this.perPage,
        searchKeyword: this.searchKeyWord ? `%${this.searchKeyWord}%` : undefined
      }
      const fetchOnlyMaterialsWithWorkflow = this.formTypeId === config.FORM_TEMPLATE_TYPE.JOF
      if (this.getTags.length > 0) {
        const body = {
          ...filter,
          tagId: this.getTags.length ? this.getTags[this.getTags.length - 1]?.id : undefined
        }
        GetMaterialMasterForBom({ filter: body, feature: this.feature }, fetchOnlyMaterialsWithWorkflow)
          .then((res) => {
            this.materialMasterData = res.core_material_master
            this.totalCount = res.core_material_master_aggregate.aggregate.count
            this.materialMasterLoading = false
          })
          .catch((err) => {
            console.log(err)
            this.materialMasterLoading = false
          })
      } else {
        GetMaterialMasterForBom({ filter, feature: this.feature }, fetchOnlyMaterialsWithWorkflow)
          .then((res) => {
            this.materialMasterData = res.core_material_master
            this.totalCount = res.core_material_master_aggregate.aggregate.count
            this.materialMasterLoading = false
          })
          .catch((err) => {
            console.log(err)
            this.materialMasterLoading = false
          })
      }
    }
  },
  created () {
    this.updateOnSearch = debounce(() => {
      this.pageNumber = 1
      this.fetchMaterialMasterData()
    }, 500)
    this.fetchMaterialMasterData()
  }
}
</script>

<style lang="scss" scoped>
.form-input {
  &--table {
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.material-table {
  input {
    font-size: var(--text-s);
    padding: 5px;
    width: 50px;
    border: 1px solid var(--brand-color-1);
  }
  th,
  td {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.footer-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.disabled-row {
  background-color: #f5f5f5;
  cursor: not-allowed;
  position: relative;
  color: #999;
  opacity: 0.6;
  &:hover::after {
    content: "Process plan not linked";
    position: absolute;
    background: #333;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: var(--text-s);
    top: -30px;
    left: 10px;
    white-space: nowrap;
    z-index: 1000;
  }
}

.disabled-row td {
  color: #999;
}

.disabled-checkbox {
  opacity: 0.3;
  cursor: not-allowed;
}
</style>
